import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Store, RefreshCw, Settings, History, Link as LinkIcon, Unlink, CheckCircle, XCircle, Clock, AlertTriangle, Loader2, ExternalLink, Package, ArrowRightLeft } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { formatDistanceToNow, format } from "date-fns";

const connectFormSchema = z.object({
  shopName: z.string().min(1, "Store name is required"),
  accessToken: z.string().min(1, "Access token is required"),
  syncSchedule: z.enum(["manual", "hourly", "6-hourly", "daily"]).default("daily"),
  syncAllCategories: z.boolean().default(true),
  selectedCategories: z.array(z.string()).optional(),
});

type ConnectFormValues = z.infer<typeof connectFormSchema>;

interface ShopifyConnection {
  id: string;
  shopName: string;
  shopUrl: string;
  syncSchedule: string;
  syncAllCategories: boolean;
  selectedCategories: string[];
  syncPricing: boolean;
  syncImages: boolean;
  syncDimensions: boolean;
  useCompareAtPrice: boolean;
  shopifyLocationId: string | null;
  isActive: boolean;
  lastSyncAt: string | null;
  lastSyncStatus: string | null;
  lastSyncError: string | null;
  nextScheduledSync: string | null;
  totalProductsSynced: number;
  totalVariantsSynced: number;
  createdAt: string;
}

interface SyncLog {
  id: string;
  triggerType: string;
  status: string;
  productsCreated: number;
  productsUpdated: number;
  variantsUpdated: number;
  inventoryUpdated: number;
  errors: string[];
  startedAt: string;
  completedAt: string | null;
  durationMs: number | null;
}

interface ShopifyLocation {
  id: string;
  name: string;
  address1: string | null;
  city: string | null;
  country: string | null;
}

export function ShopifyIntegration() {
  const { toast } = useToast();
  const [showConnectDialog, setShowConnectDialog] = useState(false);
  const [showDisconnectDialog, setShowDisconnectDialog] = useState(false);

  const { data: connectionData, isLoading: connectionLoading, error: connectionError } = useQuery<{ connected: boolean; connection?: ShopifyConnection }>({
    queryKey: ["/api/shopify/connection"],
    staleTime: 0,
  });

  const { data: categoriesData } = useQuery<{ categories: Array<{ id: string; name: string }> }>({
    queryKey: ["/api/categories"],
  });

  const { data: syncLogsData, isLoading: logsLoading } = useQuery<{ logs: SyncLog[] }>({
    queryKey: ["/api/shopify/sync-logs"],
    enabled: connectionData?.connected || false,
    refetchInterval: connectionData?.connection?.lastSyncStatus === 'in_progress' ? 5000 : false,
  });

  const { data: locationsData } = useQuery<{ locations: ShopifyLocation[] }>({
    queryKey: ["/api/shopify/locations"],
    enabled: connectionData?.connected || false,
  });

  const connection = connectionData?.connection;
  const categories = categoriesData?.categories || [];
  const syncLogs = syncLogsData?.logs || [];
  const locations = locationsData?.locations || [];

  const connectForm = useForm<ConnectFormValues>({
    resolver: zodResolver(connectFormSchema),
    defaultValues: {
      shopName: "",
      accessToken: "",
      syncSchedule: "daily",
      syncAllCategories: true,
      selectedCategories: [],
    },
  });

  const connectMutation = useMutation({
    mutationFn: async (values: ConnectFormValues) => {
      return await apiRequest("POST", "/api/shopify/connect", values);
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/shopify/connection"] });
      setShowConnectDialog(false);
      connectForm.reset();
      toast({
        title: "Connected to Shopify",
        description: `Successfully connected to ${data.connection?.shopName || 'your store'}`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Connection Failed",
        description: error.message || "Failed to connect to Shopify store",
        variant: "destructive",
      });
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: async () => {
      return await apiRequest("DELETE", "/api/shopify/connection");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/shopify/connection"] });
      setShowDisconnectDialog(false);
      toast({
        title: "Disconnected",
        description: "Shopify store has been disconnected",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to disconnect Shopify store",
        variant: "destructive",
      });
    },
  });

  const syncMutation = useMutation({
    mutationFn: async () => {
      return await apiRequest("POST", "/api/shopify/sync");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/shopify/connection"] });
      queryClient.invalidateQueries({ queryKey: ["/api/shopify/sync-logs"] });
      toast({
        title: "Sync Started",
        description: "Inventory sync has been initiated. Check logs for progress.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Sync Failed",
        description: error.message || "Failed to start sync",
        variant: "destructive",
      });
    },
  });

  const updateSettingsMutation = useMutation({
    mutationFn: async (settings: Partial<ShopifyConnection>) => {
      return await apiRequest("PATCH", "/api/shopify/connection", settings);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/shopify/connection"] });
      toast({
        title: "Settings Updated",
        description: "Sync settings have been updated",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update settings",
        variant: "destructive",
      });
    },
  });

  const getStatusBadge = (status: string | null) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-100 text-green-800"><CheckCircle className="h-3 w-3 mr-1" />Completed</Badge>;
      case 'in_progress':
        return <Badge className="bg-blue-100 text-blue-800"><Loader2 className="h-3 w-3 mr-1 animate-spin" />In Progress</Badge>;
      case 'failed':
        return <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" />Failed</Badge>;
      case 'pending':
        return <Badge variant="secondary"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
      default:
        return <Badge variant="outline">Never</Badge>;
    }
  };

  if (connectionLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (connectionError) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12 text-center">
          <AlertTriangle className="h-12 w-12 text-yellow-500 mb-4" />
          <p className="text-muted-foreground">Unable to load Shopify connection status</p>
        </CardContent>
      </Card>
    );
  }

  if (!connectionData?.connected) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Store className="h-5 w-5" />
            Shopify Integration
          </CardTitle>
          <CardDescription>
            Connect your Shopify store to automatically sync your OpenBox inventory
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center py-8 text-center">
            <Store className="h-16 w-16 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">Not Connected</h3>
            <p className="text-muted-foreground mb-6 max-w-md">
              Connect your Shopify store to automatically sync products, inventory levels, and pricing. 
              Choose which categories to sync and set your preferred schedule.
            </p>
            
            <Dialog open={showConnectDialog} onOpenChange={setShowConnectDialog}>
              <DialogTrigger asChild>
                <Button data-testid="button-connect-shopify">
                  <LinkIcon className="h-4 w-4 mr-2" />
                  Connect Shopify Store
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                  <DialogTitle>Connect Shopify Store</DialogTitle>
                  <DialogDescription>
                    Enter your Shopify store details to enable inventory sync
                  </DialogDescription>
                </DialogHeader>
                <Form {...connectForm}>
                  <form onSubmit={connectForm.handleSubmit((values) => connectMutation.mutate(values))} className="space-y-4">
                    <FormField
                      control={connectForm.control}
                      name="shopName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Store Name</FormLabel>
                          <FormControl>
                            <div className="flex items-center">
                              <Input 
                                placeholder="your-store" 
                                {...field} 
                                className="rounded-r-none"
                                data-testid="input-shop-name"
                              />
                              <span className="px-3 py-2 bg-muted border border-l-0 rounded-r-md text-sm text-muted-foreground">
                                .myshopify.com
                              </span>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={connectForm.control}
                      name="accessToken"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Admin API Access Token</FormLabel>
                          <FormControl>
                            <Input 
                              type="password" 
                              placeholder="shpat_xxxxx..." 
                              {...field}
                              data-testid="input-access-token"
                            />
                          </FormControl>
                          <FormDescription>
                            Create an app in Shopify Admin → Settings → Apps → Develop apps
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={connectForm.control}
                      name="syncSchedule"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Sync Schedule</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger data-testid="select-sync-schedule">
                                <SelectValue placeholder="Select schedule" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="manual">Manual Only</SelectItem>
                              <SelectItem value="hourly">Every Hour</SelectItem>
                              <SelectItem value="6-hourly">Every 6 Hours</SelectItem>
                              <SelectItem value="daily">Daily</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={connectForm.control}
                      name="syncAllCategories"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                          <div className="space-y-0.5">
                            <FormLabel>Sync All Categories</FormLabel>
                            <FormDescription>
                              Sync all product categories or select specific ones
                            </FormDescription>
                          </div>
                          <FormControl>
                            <Switch
                              checked={field.value}
                              onCheckedChange={field.onChange}
                              data-testid="switch-sync-all-categories"
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    {!connectForm.watch("syncAllCategories") && (
                      <FormField
                        control={connectForm.control}
                        name="selectedCategories"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Select Categories</FormLabel>
                            <div className="space-y-2">
                              {categories.map((category) => (
                                <div key={category.id} className="flex items-center space-x-2">
                                  <Checkbox
                                    id={`category-${category.id}`}
                                    checked={field.value?.includes(category.id)}
                                    onCheckedChange={(checked) => {
                                      const current = field.value || [];
                                      if (checked) {
                                        field.onChange([...current, category.id]);
                                      } else {
                                        field.onChange(current.filter((id: string) => id !== category.id));
                                      }
                                    }}
                                    data-testid={`checkbox-category-${category.id}`}
                                  />
                                  <Label htmlFor={`category-${category.id}`}>{category.name}</Label>
                                </div>
                              ))}
                            </div>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}
                    <DialogFooter>
                      <Button type="submit" disabled={connectMutation.isPending} data-testid="button-submit-connect">
                        {connectMutation.isPending ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Connecting...
                          </>
                        ) : (
                          "Connect Store"
                        )}
                      </Button>
                    </DialogFooter>
                  </form>
                </Form>
              </DialogContent>
            </Dialog>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Store className="h-5 w-5" />
              Shopify Integration
            </CardTitle>
            <CardDescription className="flex items-center gap-2 mt-1">
              Connected to{" "}
              <a 
                href={connection?.shopUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary hover:underline inline-flex items-center gap-1"
              >
                {connection?.shopName}.myshopify.com
                <ExternalLink className="h-3 w-3" />
              </a>
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={connection?.isActive ? "default" : "secondary"}>
              {connection?.isActive ? "Active" : "Paused"}
            </Badge>
            <AlertDialog open={showDisconnectDialog} onOpenChange={setShowDisconnectDialog}>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowDisconnectDialog(true)}
                data-testid="button-disconnect-shopify"
              >
                <Unlink className="h-4 w-4 mr-1" />
                Disconnect
              </Button>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Disconnect Shopify Store?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will stop all inventory syncing to your Shopify store. Products already created in Shopify will not be deleted.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => disconnectMutation.mutate()}
                    disabled={disconnectMutation.isPending}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    {disconnectMutation.isPending ? "Disconnecting..." : "Disconnect"}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="overview" data-testid="tab-overview">
              <Package className="h-4 w-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings">
              <Settings className="h-4 w-4 mr-2" />
              Settings
            </TabsTrigger>
            <TabsTrigger value="logs" data-testid="tab-logs">
              <History className="h-4 w-4 mr-2" />
              Sync History
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4 mt-4">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Products Synced</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{connection?.totalProductsSynced || 0}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Variants Synced</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{connection?.totalVariantsSynced || 0}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Last Sync</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {connection?.lastSyncAt 
                      ? formatDistanceToNow(new Date(connection.lastSyncAt), { addSuffix: true })
                      : "Never"
                    }
                  </div>
                  {connection?.lastSyncStatus && (
                    <div className="mt-1">{getStatusBadge(connection.lastSyncStatus)}</div>
                  )}
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Next Sync</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {connection?.syncSchedule === "manual" 
                      ? "Manual"
                      : connection?.nextScheduledSync 
                        ? formatDistanceToNow(new Date(connection.nextScheduledSync), { addSuffix: true })
                        : "Not scheduled"
                    }
                  </div>
                </CardContent>
              </Card>
            </div>

            {connection?.lastSyncError && (
              <Card className="border-destructive">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2 text-destructive">
                    <AlertTriangle className="h-4 w-4" />
                    Last Sync Error
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">{connection.lastSyncError}</p>
                </CardContent>
              </Card>
            )}

            <div className="flex gap-2">
              <Button 
                onClick={() => syncMutation.mutate()}
                disabled={syncMutation.isPending || connection?.lastSyncStatus === 'in_progress'}
                data-testid="button-sync-now"
              >
                {syncMutation.isPending || connection?.lastSyncStatus === 'in_progress' ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Syncing...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Sync Now
                  </>
                )}
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="settings" className="space-y-4 mt-4">
            <div className="space-y-4">
              <div className="flex items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <Label className="text-base">Active</Label>
                  <p className="text-sm text-muted-foreground">Enable or disable inventory syncing</p>
                </div>
                <Switch
                  checked={connection?.isActive}
                  onCheckedChange={(checked) => updateSettingsMutation.mutate({ isActive: checked })}
                  data-testid="switch-active"
                />
              </div>

              <div className="rounded-lg border p-4 space-y-3">
                <Label className="text-base">Sync Schedule</Label>
                <Select 
                  value={connection?.syncSchedule || "manual"}
                  onValueChange={(value) => updateSettingsMutation.mutate({ syncSchedule: value as any })}
                >
                  <SelectTrigger data-testid="select-schedule">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">Manual Only</SelectItem>
                    <SelectItem value="hourly">Every Hour</SelectItem>
                    <SelectItem value="6-hourly">Every 6 Hours</SelectItem>
                    <SelectItem value="daily">Daily</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Separator />

              <div className="space-y-3">
                <Label className="text-base">Sync Options</Label>
                
                <div className="flex items-center justify-between rounded-lg border p-3">
                  <div className="space-y-0.5">
                    <Label>Sync Pricing</Label>
                    <p className="text-sm text-muted-foreground">Update product prices in Shopify</p>
                  </div>
                  <Switch
                    checked={connection?.syncPricing}
                    onCheckedChange={(checked) => updateSettingsMutation.mutate({ syncPricing: checked })}
                    data-testid="switch-sync-pricing"
                  />
                </div>

                <div className="flex items-center justify-between rounded-lg border p-3">
                  <div className="space-y-0.5">
                    <Label>Sync Images</Label>
                    <p className="text-sm text-muted-foreground">Update product images in Shopify</p>
                  </div>
                  <Switch
                    checked={connection?.syncImages}
                    onCheckedChange={(checked) => updateSettingsMutation.mutate({ syncImages: checked })}
                    data-testid="switch-sync-images"
                  />
                </div>

                <div className="flex items-center justify-between rounded-lg border p-3">
                  <div className="space-y-0.5">
                    <Label>Sync Dimensions</Label>
                    <p className="text-sm text-muted-foreground">Add product dimensions as metafields</p>
                  </div>
                  <Switch
                    checked={connection?.syncDimensions}
                    onCheckedChange={(checked) => updateSettingsMutation.mutate({ syncDimensions: checked })}
                    data-testid="switch-sync-dimensions"
                  />
                </div>

                <div className="flex items-center justify-between rounded-lg border p-3">
                  <div className="space-y-0.5">
                    <Label>Use Compare-at Price</Label>
                    <p className="text-sm text-muted-foreground">Set MSRP as compare-at price for sale display</p>
                  </div>
                  <Switch
                    checked={connection?.useCompareAtPrice}
                    onCheckedChange={(checked) => updateSettingsMutation.mutate({ useCompareAtPrice: checked })}
                    data-testid="switch-compare-price"
                  />
                </div>
              </div>

              <Separator />

              <div className="space-y-3">
                <Label className="text-base">Category Selection</Label>
                <div className="flex items-center justify-between rounded-lg border p-3">
                  <div className="space-y-0.5">
                    <Label>Sync All Categories</Label>
                    <p className="text-sm text-muted-foreground">Sync products from all categories</p>
                  </div>
                  <Switch
                    checked={connection?.syncAllCategories}
                    onCheckedChange={(checked) => updateSettingsMutation.mutate({ syncAllCategories: checked })}
                    data-testid="switch-all-categories"
                  />
                </div>

                {!connection?.syncAllCategories && (
                  <div className="rounded-lg border p-3 space-y-2">
                    <Label>Selected Categories</Label>
                    <div className="space-y-2">
                      {categories.map((category) => (
                        <div key={category.id} className="flex items-center space-x-2">
                          <Checkbox
                            id={`settings-category-${category.id}`}
                            checked={connection?.selectedCategories?.includes(category.id)}
                            onCheckedChange={(checked) => {
                              const current = connection?.selectedCategories || [];
                              const newCategories = checked
                                ? [...current, category.id]
                                : current.filter((id: string) => id !== category.id);
                              updateSettingsMutation.mutate({ selectedCategories: newCategories });
                            }}
                            data-testid={`checkbox-settings-category-${category.id}`}
                          />
                          <Label htmlFor={`settings-category-${category.id}`}>{category.name}</Label>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {locations.length > 0 && (
                <>
                  <Separator />
                  <div className="space-y-3">
                    <Label className="text-base">Inventory Location</Label>
                    <p className="text-sm text-muted-foreground">Select which Shopify location to update inventory levels</p>
                    <Select
                      value={connection?.shopifyLocationId || ""}
                      onValueChange={(value) => updateSettingsMutation.mutate({ shopifyLocationId: value })}
                    >
                      <SelectTrigger data-testid="select-location">
                        <SelectValue placeholder="Select a location" />
                      </SelectTrigger>
                      <SelectContent>
                        {locations.map((location) => (
                          <SelectItem key={location.id} value={location.id}>
                            {location.name} {location.city && `(${location.city})`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
            </div>
          </TabsContent>

          <TabsContent value="logs" className="mt-4">
            {logsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : syncLogs.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <History className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No sync history yet</p>
              </div>
            ) : (
              <ScrollArea className="h-[400px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Products</TableHead>
                      <TableHead className="text-right">Variants</TableHead>
                      <TableHead className="text-right">Duration</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {syncLogs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell className="font-medium">
                          {format(new Date(log.startedAt), "MMM d, yyyy HH:mm")}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {log.triggerType === 'manual' ? 'Manual' : 'Scheduled'}
                          </Badge>
                        </TableCell>
                        <TableCell>{getStatusBadge(log.status)}</TableCell>
                        <TableCell className="text-right">
                          {log.productsCreated + log.productsUpdated}
                        </TableCell>
                        <TableCell className="text-right">{log.variantsUpdated}</TableCell>
                        <TableCell className="text-right">
                          {log.durationMs ? `${(log.durationMs / 1000).toFixed(1)}s` : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
